import * as React from 'react';
import type {ReactNode} from 'react';
import {more} from '@sail/icons/react/Icon';
import {createFocusManager} from '@sail/react-aria';
import {useConstant} from '@sail/engine';
import {view} from '../view';
import {css} from '../css';
import {Button} from './Button';
import {Icon} from './Icon';
import {Menu, MenuGroup, MenuItem} from './Menu';
import {useOverflowCollapse} from '../../components/hooks/useOverflowCollapse';

type TextDirection = 'ltr' | 'rtl';

export interface ButtonGroupProps {
  /**
   * Controls which axis the button group should span.
   * @external
   */
  direction?: 'row' | 'column';

  /**
   * Controls whether or not buttons within the group collapse when there isn't enough space to display them without overflowing.
   * @external
   */
  collapse?: 'auto' | 'none';

  /**
   * Allows overriding the trigger element used for an overflow menu. Must be a component that supports press events.
   * @external
   */
  menuTrigger?: ReactNode;
}

const DEFAULT_MENU_TRIGGER = (
  <Button aria-label="…">
    <Icon icon={more} />
  </Button>
);

interface RovingTabIndexProps {
  textDirection?: TextDirection;
  wrap?: boolean;
  ref: React.RefObject<HTMLElement>;
}

// TODO(cina): see about consolidating this with Chip's focus handling intents
function useRovingTabIndex({
  textDirection = 'ltr',
  wrap = true,
  ref,
}: RovingTabIndexProps) {
  const focusManager = useConstant(() => createFocusManager(ref));

  React.useEffect(() => {
    const element = ref.current;

    function focusHandler(event: FocusEvent) {
      if (element) {
        element
          .querySelectorAll<HTMLElement>('[tabindex]')
          .forEach((tabbable) => {
            tabbable.tabIndex = tabbable === event.target ? 0 : -1;
          });
      }
    }

    function blurHandler(event: FocusEvent) {
      if (
        element &&
        event.target &&
        event.relatedTarget &&
        !element.contains(event.target as Node)
      ) {
        (event.relatedTarget as HTMLElement).tabIndex = 0;
      }
    }

    if (element) {
      element.addEventListener('focus', focusHandler, {capture: true});
      element.addEventListener('blur', blurHandler, {capture: true});
      return () => {
        element.removeEventListener('focus', focusHandler, {capture: true});
        element.removeEventListener('blur', blurHandler, {capture: true});
      };
    }
  }, [ref]);

  React.useEffect(() => {
    const element = ref.current;

    function keydownHandler(event: KeyboardEvent) {
      if (event.key.startsWith('Arrow')) {
        event.preventDefault();

        if (
          (event.key === 'ArrowUp' || event.key === 'ArrowDown') &&
          document.activeElement?.getAttribute('aria-haspopup')
        ) {
          return;
        }

        if (
          event.key === 'ArrowUp' ||
          (event.key === 'ArrowLeft' && textDirection !== 'rtl') ||
          (event.key === 'ArrowRight' && textDirection === 'rtl')
        ) {
          focusManager.focusPrevious({wrap});
        } else {
          focusManager.focusNext({wrap});
        }
      }
    }

    if (element) {
      element.addEventListener('keydown', keydownHandler);
      return () => {
        element.removeEventListener('keydown', keydownHandler);
      };
    }
  }, [ref, focusManager, textDirection, wrap]);
}

/**
 * @external
 */
export const ButtonGroup = view.div.extend<ButtonGroupProps>(
  (
    {
      direction = 'row',
      collapse = 'auto',
      dir = 'ltr',
      menuTrigger = DEFAULT_MENU_TRIGGER,
      children,
      ...props
    },
    View,
  ) => {
    const ref = React.useRef<HTMLDivElement>(null);

    useRovingTabIndex({
      ref,
      textDirection: dir as TextDirection,
      wrap: true,
    });

    const {hiddenChildrenIndexes} = useOverflowCollapse({
      ref,
      menuTrigger,
      collapse,
      direction,
      textDirection: dir as TextDirection,
    });

    return (
      <View
        ref={ref}
        uses={[
          css({
            stack: direction === 'row' ? 'x' : 'y',
            alignX: direction === 'row' ? 'start' : 'center',
            alignItems: css.value('stretch'),
            gap: 'small',
          }),
          props,
        ]}
        role="toolbar"
        aria-orientation={direction === 'row' ? 'horizontal' : 'vertical'}
      >
        {React.Children.map(children, (child) =>
          React.isValidElement<{'data-priority': number; type: string}>(
            child,
          ) && child.type === Button
            ? React.cloneElement<{'data-priority': number}>(child, {
                'data-priority': child.props.type === 'primary' ? 1 : 2,
              })
            : child,
        )}

        {collapse === 'auto' ? (
          <Menu trigger={menuTrigger}>
            {React.Children.map(children, (child, index) => {
              if (
                React.isValidElement(child) &&
                child.type === Button &&
                hiddenChildrenIndexes.has(index)
              ) {
                const menuItem = (
                  // TODO(cina): figure out what to do with Buttons that have href instead of onPress
                  // eslint-disable-next-line react/jsx-handler-names
                  <MenuItem onAction={child.props.onPress}>
                    {child.props.children}
                  </MenuItem>
                );

                return child.props.type === 'primary' ? (
                  <MenuGroup>{menuItem}</MenuGroup>
                ) : (
                  menuItem
                );
              } else {
                return null;
              }
            })}
          </Menu>
        ) : null}
      </View>
    );
  },
);

ButtonGroup.displayName = 'ButtonGroup';
